import React, {
  useCallback,
  useEffect,
  useReducer,
  useState,
  useMemo,
} from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { logEvent } from 'services/amplitude';
import Form from 'components/common/formElements/form';
import { useDispatch, connect } from 'react-redux';
import {
  createScholar,
  createAnetapack,
  getUnits,
  editScholar,
  clearUnitsErrors,
} from 'actions/scholars';
import _ from 'lodash';
import { IScholars, IScholarsRequestData, ICreateAnetapack } from 'types';
import { IRootState } from 'store';
import { defaultMapTemplate, defaultMapType } from 'utils/constants/map';
import TeacherForm from './Form/teacherform';
import { ChangeActionPayload, initialState, profileReducer } from './reducer';
import ProfileForm from './Form';
import ProfilePhoto from './Photo';
import styles from './styles.module.scss';

const mapStateToProps = (state: IRootState) => ({
  scholars: state.scholars,
  users: state.users,
});

type ICreateProfile = ReturnType<typeof mapStateToProps>;
interface ILocationState {
  mode: string;
}

const CreateProfile: React.FC<ICreateProfile> = ({ scholars, users }) => {
  const history = useHistory();
  const location = useLocation<ILocationState>();
  const [form, formDispatch] = useReducer(profileReducer, initialState);
  const [activeKid, setActiveKid] = useState<IScholars>();
  const [name, setName] = useState('');
  const [error, setError] = useState({});
  const { permissionToEditTimetable, permissionToEditResources, avatar } = form;

  const [avatarFile, setAvatarFile] = useState<File | undefined>();
  const [defaultAvatar, setDefaultAvatar] = useState('');

  const [skills, setSkills] = useState<string[]>([]);
  const [grades, setGrades] = useState<string[]>([]);
  const [interests, setInterests] = useState<string[]>([]);
  const dispatch = useDispatch();

  const handleChange = useCallback((payload: ChangeActionPayload) => {
    formDispatch({ type: 'UPDATE', payload });
  }, []);

  const handleSetName = (e: React.FormEvent<HTMLInputElement>) => {
    setName(e.currentTarget.value);
  };
  useEffect(() => {
    dispatch(getUnits());
    dispatch(clearUnitsErrors());
  }, [dispatch]);

  useEffect(() => {
    if (location.state?.mode !== 'create') {
      if (!_.isEmpty(scholars.units) && users.userData?.activeScholarId) {
        const unit = _.find(
          scholars.units,
          (e) => e.id === users.userData?.activeScholarId
        );
        if (unit) {
          setActiveKid(unit);
          handleChange({
            permissionToEditResources: unit.settings?.permissionToEditResources,
            permissionToEditTimetable: unit.settings?.permissionToEditTimetable,
            avatar: defaultAvatar,
          });
        }
      }
    }
  }, [
    scholars,
    location,
    handleChange,
    defaultAvatar,
    users.userData?.activeScholarId,
  ]);

  useEffect(() => {
    if (location.state?.mode !== 'create') {
      if (!_.isEmpty(scholars.units) && users.userData?.activeScholarId) {
        const unit = _.find(
          scholars.units,
          (e) => e.id === users.userData?.activeScholarId
        );
        if (unit) {
          if (unit.skills && unit.interests) {
            setSkills(unit.skills);
            setInterests(unit.interests);
          }
          setName(unit.name);
          setGrades(unit.grades);
        }
      }
    }
  }, [scholars, location, handleChange, users.userData?.activeScholarId]);

  const allowToRedirect =
    (scholars.units && scholars.units?.length <= 4) || !location.state?.mode;

  const isFormValid = useMemo(() => {
    let result = true;
    if (users.userData?.type === 'teacher') {
      if (
        !_.isEmpty(interests) &&
        !_.isEmpty(grades) &&
        !_.isEmpty(skills) &&
        name !== ''
      ) {
        result = false;
      } else {
        result = true;
      }
    }
    if (users.userData?.type === 'parent') {
      if (!_.isEmpty(grades) && name !== '') {
        result = false;
      } else {
        result = true;
      }
    }
    return result;
  }, [grades, interests, name, skills, users.userData?.type]);

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      let errors = {};
      if (name === '') {
        errors = { ...errors, name: true };
      }
      if (_.isEmpty(grades)) {
        errors = { ...errors, grades: true };
      }
      if (users.userData?.type === 'teacher') {
        if (_.isEmpty(skills)) {
          errors = { ...errors, skills: true };
        }
        if (_.isEmpty(interests)) {
          errors = { ...errors, interests: true };
        }
      }

      setError(errors);
      if (users.userData?.type === 'parent') {
        if (!_.isEmpty(grades) && name !== '') {
          if (activeKid && !location.state?.mode) {
            const editData: IScholarsRequestData = {
              grades,
              name,
              settings: {
                permissionToEditResources,
                permissionToEditTimetable,
                mapTemplate: defaultMapTemplate,
                mapType: defaultMapType,
              },
            };
            await dispatch(
              editScholar(activeKid.id, editData, avatarFile, defaultAvatar)
            );
          } else {
            await dispatch(
              createScholar(
                {
                  settings: {
                    permissionToEditResources: form.permissionToEditResources,
                    permissionToEditTimetable: form.permissionToEditTimetable,
                    mapTemplate: defaultMapTemplate,
                    mapType: defaultMapType,
                  },
                  grades,
                  name,
                },
                avatarFile,
                defaultAvatar
              )
            );
          }
          dispatch(getUnits());
          if (scholars && allowToRedirect) {
            history.push('/onboarding/choose-map/');
            logEvent('ProfilePageNext');
          }
        }
      } else {
        const requestData: ICreateAnetapack = {
          name,
          grades: _.uniq(grades),
          skills: _.uniq(skills),
          interests: _.uniq(interests),
        };
        if (!isFormValid) {
          if (activeKid && !location.state?.mode) {
            await dispatch(
              editScholar(activeKid.id, requestData, avatarFile, defaultAvatar)
            );
          } else {
            await dispatch(
              createAnetapack(requestData, avatarFile, defaultAvatar)
            );
          }
          dispatch(getUnits());
        }
        if (scholars && Object.keys(errors).length === 0) {
          history.push('/onboarding/timetable');
          logEvent('ProfilePageNext');
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      name,
      activeKid,
      location.state?.mode,
      dispatch,
      scholars,
      permissionToEditResources,
      permissionToEditTimetable,
      users.userData?.type,
      avatarFile,
      defaultAvatar,
      form.permissionToEditResources,
      form.permissionToEditTimetable,
      history,
      interests,
      grades,
      skills,
      isFormValid,
    ]
  );
  const handleDefaultAvatar = (url: string) => {
    setDefaultAvatar(url);
  };

  return (
    <Form className={`${styles.rootMargin} ${styles.root}`} onSubmit={onSubmit}>
      {users.userData?.type === 'parent' ? (
        <ProfileForm
          permissionToEditTimetable={permissionToEditTimetable}
          permissionToEditResources={permissionToEditResources}
          onChange={handleChange}
          handleSetName={handleSetName}
          name={name}
          grade={grades}
          setGrade={setGrades}
          errors={error}
          requestError={scholars.errors}
        />
      ) : (
        <TeacherForm
          handleSetName={handleSetName}
          name={name}
          grade={grades}
          skills={skills}
          interests={interests}
          setInterests={setInterests}
          setSkills={setSkills}
          setGrade={setGrades}
          errors={error}
          requestError={scholars.anetapackError}
        />
      )}
      <ProfilePhoto
        onChange={setAvatarFile}
        handleDefaultAvatar={handleDefaultAvatar}
        avatarUrl={activeKid?.avatar || avatar}
        userType={users.userData?.type || ''}
      />
    </Form>
  );
};

export default connect(mapStateToProps)(CreateProfile);
